import { z } from 'zod';

export const cipcSchema = z.object({
    reg1: z.string().length(4, "Reg1 must be 4 chars").optional().or(z.literal('')),
    reg2: z.string().length(6, "Reg2 must be 6 chars").optional().or(z.literal('')),
    reg3: z.string().length(2, "Reg3 must be 2 chars").optional().or(z.literal('')),
    businessName: z.string().min(3, "Business Name must be at least 3 chars").max(60, "Name too long").optional().or(z.literal('')),
    vatNo: z.string().regex(/^\d+$/, "VAT Number must be numeric").optional().or(z.literal('')),
    solePropIdNo: z.string().length(13, "ID Number must be 13 chars").regex(/^\d+$/, "ID Number must be numeric").optional().or(z.literal('')),
    yourReference: z.string().optional().or(z.literal('')),
    voucherCode: z.string().optional().or(z.literal('')),
}).superRefine((data, ctx) => {
    const reg1 = data.reg1 === '' ? undefined : data.reg1;
    const reg2 = data.reg2 === '' ? undefined : data.reg2;
    const reg3 = data.reg3 === '' ? undefined : data.reg3;
    const businessName = data.businessName === '' ? undefined : data.businessName;
    const vatNo = data.vatNo === '' ? undefined : data.vatNo;
    const solePropIdNo = data.solePropIdNo === '' ? undefined : data.solePropIdNo;

    const hasReg = reg1 && reg2 && reg3;
    const hasName = !!businessName;
    const hasVat = !!vatNo;
    const hasID = !!solePropIdNo;

    if (!hasReg && !hasName && !hasVat && !hasID) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please provide at least one search criteria: Registration Number (all 3 parts), Business Name, VAT Number, or Sole Prop ID",
            path: ['businessName'],
        });
    }

    if (reg1 || reg2 || reg3) {
        if (!reg1) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Reg1 missing", path: ['reg1'] });
        if (!reg2) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Reg2 missing", path: ['reg2'] });
        if (!reg3) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Reg3 missing", path: ['reg3'] });
    }
});

export type CIPCReportPayload = z.infer<typeof cipcSchema>;

// Typed match contract — mirrors CipcMatchItem Pydantic schema
export interface CipcMatchItem {
    commercial_id: string | null;
    registration_no: string | null;
    business_name: string | null;
    enquiry_id: string;
    enquiry_result_id: string;
    reference: string | null;
}

export interface CipcMatchResponse {
    success: boolean;
    message: string;
    log_id: string;
    matches: CipcMatchItem[];
}

export interface CipcResultPayload {
    enquiryId: number;
    enquiryResultId: number;
    yourReference?: string;
    voucherCode?: string;
}

export interface CIPCReportResponse {
    message: string;
    success: boolean;
    log_id: string;
    report_data: any;
}

const MATCH_URL = '/verification/cipc-report/match';
const RESULT_URL = '/verification/cipc-report/result';

export const cipcReportService = {
    createPayload: (formData: z.infer<typeof cipcSchema>): CIPCReportPayload => ({ ...formData }),

    getMatch: async (payload: CIPCReportPayload): Promise<CipcMatchResponse> => {
        const axios = (await import('../axiosConfig')).default;
        const response = await axios.post<CipcMatchResponse>(MATCH_URL, payload);
        return response.data;
    },

    getResult: async (payload: CipcResultPayload): Promise<CIPCReportResponse> => {
        const axios = (await import('../axiosConfig')).default;
        const response = await axios.post<CIPCReportResponse>(RESULT_URL, payload);
        return response.data;
    },
};
